import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCog, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/**
 * Aviso permanente mientras un admin esta viendo la app como otro usuario.
 *
 * Es deliberadamente llamativo y no se puede cerrar: el riesgo real de esta
 * funcionalidad es olvidarse de que esta activa y creer que los datos que se ven
 * (o se modifican) son los propios.
 */
export const ImpersonationBanner: React.FC = () => {
  const { user, isImpersonating, stopImpersonation } = useAuth();
  const [isStopping, setIsStopping] = useState(false);

  if (!isImpersonating || !user) return null;

  const handleStop = async () => {
    setIsStopping(true);
    try {
      await stopImpersonation();
      window.location.href = '/admin/users';
    } catch {
      setIsStopping(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -60 }}
        animate={{ y: 0 }}
        className="sticky top-0 z-[60] w-full"
        style={{ background: 'linear-gradient(135deg, #B45309 0%, #92400E 100%)' }}
      >
        <div className="px-4 py-2.5 flex items-center gap-3 max-w-5xl mx-auto">
          <UserCog size={18} className="text-white flex-shrink-0" />
          <p className="text-sm text-white flex-1 min-w-0 truncate">
            Estás viendo la app como <strong>{user.name}</strong>
            {user.adminName && (
              <span className="hidden sm:inline opacity-80"> · admin: {user.adminName}</span>
            )}
          </p>
          <button
            onClick={handleStop}
            disabled={isStopping}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-colors disabled:opacity-60 flex-shrink-0"
          >
            {isStopping ? (
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <LogOut size={14} />
            )}
            <span className="hidden sm:inline">Volver a mi cuenta</span>
            <span className="sm:hidden">Salir</span>
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
